<template>
    <page class="property-categories" :tabs="tabs" :busy="busy">
        <div class="text-primary font-italic p-2 mb-2">
            <font-awesome-icon :icon="['fas', 'circle-info']"></font-awesome-icon>
            Drag rows to change the order.
        </div>
        <grid :rowData="items" :columnDefs="columnDefs" :saveRowOrder="saveRowOrder" :enableCellTextSelection="false">
            <responsive-button
                @click="$bvModal.show('create-category')"
                variant="primary"
                title="Add Category"
                :icon="['fas', 'plus']"
            >
                Add Category
            </responsive-button>
        </grid>

        <validation-observer v-slot="{ meta, setValues }">
            <modal
                ok-title="Save"
                id="create-category"
                title="Add Category"
                @ok.prevent="createCategory"
                :ok-disabled="!meta.valid"
                @hidden="categoryName = ''"
            >
                <validation-provider name="name" v-slot="{ errors }" rules="required" >
                    <form-input
                        :floating-label="labelName"
                        v-model="categoryName"
                        @input="(val) => setValues({ name: val })"
                        :error="errors[0]"
                    ></form-input>
                </validation-provider>
            </modal>
        </validation-observer>
    </page>
</template>

<script>
    import Page from '../../generic/page/page.vue';
    import Grid from '../../generic/grid/grid.vue';
    import ActionsCellRenderer from './actions-cell-renderer.vue';
    import API from '../../../api';
    import {
        PROPERTY_CATEGORIES_LIST_ENDPOINT,
        PROPERTY_CATEGORIES_UPDATE_DELETE_ENDPOINT,
        PROPERTY_CATEGORIES_ORDER_ENDPOINT,
    } from '../../../endpoints';
    import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
    import Modal from '../../generic/modal/modal.vue';
    import FormInput from '../../generic/form-input/form-input.vue';
    import { removeScrollMixin } from '../../../mixins/remove-scroll-mixin.js';

    export default {
        name: 'PropertyCategoriesList',
        components: {
            Page,
            Grid,
            ResponsiveButton,
            Modal,
            FormInput,
            actionsCellRenderer: ActionsCellRenderer,
        },
        mixins: [removeScrollMixin],
        data() {
            return {
                categoryName: null,
                labelName: 'Category name',
                tabs: [
                    {
                        title: 'Manage Categories',
                        id: 'property-categories',
                        active: true,
                    },
                ],
                busy: null,
                items: [],
                columnDefs: [
                    {
                        field: 'order',
                        headerName: 'Order',
                        flex: 1,
                        maxWidth: 200,
                        cellClass: 'first-drag-column',
                        sort: 'asc',
                        rowDrag: true,
                        rowDragText: ({ defaultTextValue, rowNode }) => `${defaultTextValue} ${rowNode.data.name}`,
                    },
                    {
                        field: 'name',
                        headerName: 'Name',
                        flex: 2,
                        resizable: false,
                        cellClass: 'user-select-none',
                        rowDrag: true,
                        rowDragText: ({ defaultTextValue, rowNode }) => `${rowNode.data.order} ${defaultTextValue}`,
                    },
                    {
                        field: '',
                        cellRenderer: 'actionsCellRenderer',
                        maxWidth: 150,
                        minWidth: 150,
                        resizable: false,
                        sortable: false,
                        cellStyle: {
                            display: 'flex',
                            justifyContent: 'center',
                        },
                        cellRendererParams: {
                            update: () => {
                                this.fetchCategories();
                            },
                        },
                    },
                ],
            };
        },
        mounted() {
            this.fetchCategories();
        },
        methods: {
            async createCategory() {
                try {
                    await API.post(PROPERTY_CATEGORIES_LIST_ENDPOINT(), {
                        name: this.categoryName,
                        labelName: this.labelName,
                    });
                    await this.fetchCategories();
                    this.$bvModal.hide('create-category');
                } catch (e) {
                    this.errorToast({ text: 'Could not create category' });
                }
            },
            async fetchCategories() {
                try {
                    this.busy = true;
                    const { data } = await API.get(PROPERTY_CATEGORIES_LIST_ENDPOINT() + 'all-categories/');
                    this.items = data;
                } catch (e) {
                    this.errorToast({ text: 'Could not load data' });
                } finally {
                    this.busy = false;
                }
            },
            async saveRowOrder({ row, order, onSuccess, onError }) {
                try {
                    onSuccess();
                    const requestBody = this.items.map((item) => {
                        return {
                            id: item.id,
                            order: item.order,
                        };
                    });
                    await API.put(PROPERTY_CATEGORIES_ORDER_ENDPOINT(), requestBody);
                    await this.fetchCategories();
                } catch (e) {
                    onError(e);
                }
            },
        },
    };
</script>
<style scoped lang="scss">
    :deep(.ag-icon-grip) {
        position: absolute;
        pointer-events: auto;
        top: 0;
        opacity: 0;
        width: 100%;
        height: 100%;
    }
    :deep(.first-drag-column .ag-icon-grip) {
        position: relative;
        top: 12px;
        opacity: 1;
    }
</style>
